// Derived hub statistics — v1's computeStats() over the viewed week + completions.
// Pure computation from existing signals; nothing here fetches.
import { computed } from '@preact/signals';
import type { CompletionRec } from './completions';
import { completions } from './completions';
import { week } from './schedule';

export interface TechBreak {
  assigned: number;
  done: number;
  missed: number;
}
export interface ActivityRow {
  id: string;
  by: string;
  status: 'done' | 'missed';
  at: string;
  development: string;
  address: string;
}
export interface HubStats {
  total: number;
  done: number;
  missed: number;
  open: number;
  pctDone: number;
  pctResolved: number;
  returns: number;
  byDay: { key: string; label: string; done: number; missed: number; left: number }[];
  byTech: Record<string, TechBreak>;
  byWho: Record<string, { done: number; missed: number }>;
  anyAssigned: boolean;
  activity: ActivityRow[];
}

export const hubStats = computed<HubStats>(() => {
  const w = week.value;
  const comps = completions.value;
  const s: HubStats = {
    total: 0,
    done: 0,
    missed: 0,
    open: 0,
    pctDone: 0,
    pctResolved: 0,
    returns: 0,
    byDay: [],
    byTech: {},
    byWho: {},
    anyAssigned: false,
    activity: [],
  };
  if (!w) return s;

  for (const d of w.days) {
    let dDone = 0;
    let dMissed = 0;
    for (const c of d.calls) {
      s.total++;
      const rec: CompletionRec | undefined = comps[c.id];
      if (c.tech) s.anyAssigned = true;
      if (rec) {
        if (rec.status === 'missed') {
          s.missed++;
          dMissed++;
        } else {
          s.done++;
          dDone++;
        }
        if (rec.needsReturn) s.returns++;
        const who = rec.by || 'Someone';
        const bw = (s.byWho[who] ??= { done: 0, missed: 0 });
        if (rec.status === 'missed') bw.missed++;
        else bw.done++;
        s.activity.push({
          id: c.id,
          by: rec.by,
          status: rec.status,
          at: rec.at,
          development: c.development,
          address: c.address,
        });
      }
      if (c.tech) {
        const bt = (s.byTech[c.tech] ??= { assigned: 0, done: 0, missed: 0 });
        bt.assigned++;
        if (rec) {
          if (rec.status === 'missed') bt.missed++;
          else bt.done++;
        }
      }
    }
    if (d.calls.length) {
      s.byDay.push({
        key: d.key,
        label: (d.label || d.key).split(',')[0]!,
        done: dDone,
        missed: dMissed,
        left: d.calls.length - dDone - dMissed,
      });
    }
  }

  s.open = s.total - s.done - s.missed;
  s.pctDone = s.total ? Math.round((s.done / s.total) * 100) : 0;
  s.pctResolved = s.total ? Math.round(((s.done + s.missed) / s.total) * 100) : 0;
  s.activity.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  return s;
});
